<script>
	import { tweened } from 'svelte/motion';
	import { cubicOut } from 'svelte/easing';
	import { fade } from 'svelte/transition';
	import * as Pancake from '@sveltejs/pancake';
	import points from './points.js';

	// The eight PDQ-8 items, verbatim. Wording is the instrument; do not
	// paraphrase it for style.
	const QUESTIONS = [
		'Had difficulty getting around in public?',
		'Had difficulty dressing yourself?',
		'Felt depressed?',
		'Had problems with your close personal relationships?',
		'Had problems with your concentration, eg. when reading or watching TV?',
		'Felt unable to communicate with people properly?',
		'Had painful muscle cramps or spasms?',
		'Felt embarrassed in public due to having Parkinson’s disease?'
	];

	// The five official response options, in order, scored 0-4.
	const OPTIONS = ['Never', 'Occasionally', 'Sometimes', 'Often', 'Always'];

	let answers = Array(QUESTIONS.length).fill(undefined);

	// NOT `bind:group={answers[i]}`. Binding a group to an array MEMBER writes
	// through without reassigning the array, so Svelte never invalidates it:
	// the badge updated but "n of 8 answered" stayed at 0 and the score came
	// out as 0/100%. Assigning and then reassigning forces the invalidation.
	function setAnswer(i, value) {
		answers[i] = value;
		answers = answers;
	}

	$: answered = answers.filter((v) => v !== undefined).length;
	$: complete = answered === QUESTIONS.length;

	// PDQ-8 Summary Index: the item total as a percentage of the maximum (32).
	// Higher means MORE impact on quality of life - the opposite direction to
	// most scores, which is exactly why the result says so in words.
	$: totalscore = complete
		? Math.round((answers.reduce((a, b) => a + b, 0) / 32) * 100)
		: NaN;

	// points.js is a cumulative curve, so the value at a score is the share of
	// people with Parkinson's scoring at or below it - the share this person
	// is above. One direction, always true.
	// points.js carries one row per integer from -33 to 100, so every reachable
	// score (0-100) has an exact match. The nearest-row fallback exists only so
	// a future edit to points.js degrades to a slightly-off percentile rather
	// than to null, and cdf() can no longer return null for any real score.
	function cdf(x) {
		let best = null;
		for (const row of points) {
			if (Math.round(+row.pdqx) === x) return +row.inty;
			if (best === null || Math.abs(+row.pdqx - x) < Math.abs(+best.pdqx - x)) best = row;
		}
		return best === null ? null : +best.inty;
	}

	// The chart plots x from 0 to 100, but points.js starts at -33: those rows
	// are the fitted distribution's left tail, and a PDQ-8 index below 0 is not
	// a score anyone can get. Pancake does not clip to the domain, so drawing
	// them ran the curve out of the plot and underneath the citation text.
	// Clipped here rather than with overflow/clip-path so the path itself stops
	// at the axis and the draw-on animation measures the visible length.
	// cdf() keeps the FULL set: it matches on exact integers, and its
	// nearest-row fallback should still see every row.
	const curvePoints = points.filter((d) => +d.pdqx >= 0);

	$: rawCdf = complete ? cdf(totalscore) : null;
	$: percentile = rawCdf === null ? null : Math.round(rawCdf * 100);

	const reduceMotion =
		typeof matchMedia !== 'undefined' &&
		matchMedia('(prefers-reduced-motion: reduce)').matches;
	const dur = (ms) => (reduceMotion ? 0 : ms);

	const shownScore = tweened(0, { duration: dur(520), easing: cubicOut });
	$: markerX = complete ? totalscore : 0;
	$: markerY = complete ? cdf(totalscore) : 0;
	$: if (complete) shownScore.set(totalscore);
	$: markerState = [{ myX: markerX, myY: markerY }];


	// Set, not animated: a smooth scroll stalls in a throttled iframe, and
	// scrollIntoView can drag the parent Webflow page. See hfsgs.svelte.
	function revealResult(el) {
		const frame = requestAnimationFrame(() => {
			const doc = el.ownerDocument;
			const win = doc.defaultView;
			const scroller = doc.scrollingElement || doc.documentElement;
			scroller.scrollTop = Math.max(
				0,
				el.getBoundingClientRect().top + win.scrollY - 12
			);
		});
		return {
			destroy() {
				cancelAnimationFrame(frame);
			}
		};
	}
</script>

<div class="pdq8">
	<header class="masthead">
		<h1>Quality of Life in Parkinson&rsquo;s&nbsp;Disease</h1>
		<p class="lede">Eight questions about the last month.</p>
	</header>

	{#each QUESTIONS as question, i}
		<fieldset class="q">
			<legend class="q-head">
				<span class="q-no" class:done={answers[i] !== undefined}>
					{#if answers[i] !== undefined}
						<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6.5 5 9.5 10 3" /></svg>
					{:else}{i + 1}{/if}
				</span>
				<span class="q-text">{question}</span>
			</legend>
			<!-- Each option carries its own label. The original was a six-column
			     table whose meaning lived in a header row you could not see once
			     you had scrolled past it - and which had no width on a phone. -->
			<div class="scale">
				{#each OPTIONS as option, v}
					<label class="opt">
						<input
							type="radio"
							name="q{i}"
							value={v}
							checked={answers[i] === v}
							on:change={() => setAnswer(i, v)}
						/>
						<span class="opt-box"><span class="dot" aria-hidden="true"></span>{option}</span>
					</label>
				{/each}
			</div>
		</fieldset>
	{/each}

	<div class="result-region" aria-live="polite">
		{#if !complete}
			<p class="progress-note" transition:fade={{ duration: dur(150) }}>
				{answered} of {QUESTIONS.length} answered
			</p>
		{:else}
			<section class="result" use:revealResult in:fade={{ duration: dur(140) }}>
				<div class="result-grid">
					<div class="scoreblock">
						<p class="result-label">Your score</p>
						<p class="score">
							<span class="score-n">{Math.round($shownScore)}</span>
							<span class="score-of">of 100</span>
						</p>
						<p class="reading">
							{#if percentile !== null}
								Higher than about <strong>{percentile}%</strong> of people with
								Parkinson&rsquo;s disease.
							{/if}
							A higher score means the condition is affecting daily life more.
						</p>
						<p class="source">
							Scale and population data:
							<cite>Jenkinson&nbsp;&amp; Fitzpatrick, 2007</cite>
						</p>
					</div>

					<figure class="chartblock">
						<div class="chart">
							<Pancake.Chart x1={0} x2={100} y1={0} y2={1.08}>
								<Pancake.Grid horizontal count={3} let:value>
									<div class="grid-line horizontal"><span>{Math.round(value * 100)}%</span></div>
								</Pancake.Grid>
								<Pancake.Grid vertical count={5} let:value>
									<span class="x-label">{value}</span>
								</Pancake.Grid>
								<Pancake.Svg>
									<Pancake.SvgLine data={curvePoints} x={(d) => +d.pdqx} y={(d) => +d.inty} let:d>
										<path class="curve" {d} />
									</Pancake.SvgLine>
								</Pancake.Svg>
								<Pancake.Svg>
									<Pancake.SvgScatterplot data={markerState} x={(d) => d.myX} y={(d) => d.myY} let:d>
										<path class="marker-halo" {d} />
										<path class="marker" {d} />
									</Pancake.SvgScatterplot>
								</Pancake.Svg>
								<Pancake.Point x={markerX} y={markerY}>
									<span class="you-label">You</span>
								</Pancake.Point>
							</Pancake.Chart>
						</div>
						<figcaption>Share of people at or below each score</figcaption>
					</figure>
				</div>
			</section>
		{/if}
	</div>
</div>

<style>
	.pdq8 {
		max-width: 880px;
		margin: 0 auto;
		padding: var(--space-md) var(--space-md) var(--space-xl);
		color: var(--ink);
		font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont,
			'Segoe UI', Roboto, sans-serif;
		font-size: 16px;
		line-height: 1.5;
	}

	/* ---- masthead ------------------------------------------------------- */
	.masthead {
		margin-bottom: var(--space-lg);
	}
	h1 {
		margin: 0 0 var(--space-sm);
		font-size: clamp(24px, 3.2vw, 32px);
		font-weight: 650;
		line-height: 1.15;
		letter-spacing: -0.015em;
		color: var(--ink);
	}
	.lede {
		margin: 0;
		max-width: 52ch;
		color: var(--text-2);
	}

	/* ---- questions ------------------------------------------------------ */
	.q {
		margin: 0 0 var(--space-lg);
		padding: 0;
		border: 0;
	}
	/* The badge is aligned to the FIRST LINE BOX, not to the baseline. Baseline
	   alignment needs a magic translateY to look right, and that offset is only
	   correct for one font size - it drifted here (16px text, not HFSGS's 18px)
	   and looked worst on the two-line questions. Matching the badge height to
	   the first line's height centres it optically at any size. */
	.q-head {
		display: flex;
		align-items: flex-start;
		gap: var(--space-sm);
		margin-bottom: var(--space-sm);
		padding: 0;
	}
	.q-no {
		flex: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1.5px solid var(--line);
		font-size: 12px;
		font-weight: 600;
		color: var(--text-2);
		transition: background-color 0.25s var(--ease-out),
			border-color 0.25s var(--ease-out);
	}
	.q-no.done {
		background: var(--teal);
		border-color: var(--teal);
		color: #fff;
	}
	.q-no svg {
		width: 12px;
		height: 12px;
	}
	.q-no svg path {
		fill: none;
		stroke: #fff;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
	.q-text {
		font-size: 16px;
		font-weight: 600;
		letter-spacing: -0.005em;
		/* 16 x 1.5 = a 24px first line, exactly the badge's height. */
		line-height: 1.5;
	}

	/* ---- the five-point scale ------------------------------------------- */
	.scale {
		display: grid;
		/* align-items: start, explicitly. A grid's default is stretch, which is
		   invisible in Chrome because an auto row hugs its content — but on iOS
		   Safari the row can come out taller than its cards (seen on a real
		   iPhone: unanswered steps rendered with cards stretched to ~450px,
		   snapping to normal once answered). Start makes each card its own
		   content height whatever the row does, so the fix does not depend on
		   which WebKit quirk produced the tall row. */
		align-items: start;
		/* grid-auto-rows: max-content, explicitly. Reproduced in real WebKit
		   (Playwright, iPhone geometry, the live Webflow modal): on first layout
		   WebKit sized these auto rows to 216px and 118px for 60px and 44px
		   cards — no rule set a height, no child was tall, the track sizing
		   itself was wrong — and a click's relayout corrected it. Seen on a real
		   iPhone as a large blank gap between cards that vanished on tap.
		   max-content sizes each row to its tallest item's content through the
		   explicit path, which was correct on first paint in the same bench. */
		grid-auto-rows: max-content;
		/* minmax(0, 1fr), not 1fr: a bare 1fr track has an implicit min-width of
		   auto, so it refuses to shrink below "Occasionally" and five of them
		   pushed the grid wider than a 280px screen. Paired with the wrap rule
		   on .opt-box so the label breaks instead of the layout. */
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: 6px;
		margin-left: 32px;
	}
	.opt {
		position: relative;
		display: block;
		cursor: pointer;
	}
	.opt input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}
	/* Desktop keeps the five-across boxes; the dot exists for the phone
	   layout below and is hidden here. */
	.dot {
		display: none;
	}
	.opt-box {
		display: flex;
		align-items: center;
		justify-content: center;
		overflow-wrap: anywhere;
		min-height: 44px; /* a real touch target */
		padding: 6px 4px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		font-size: 12px;
		line-height: 1.25;
		text-align: center;
		color: var(--text-2);
		transition: border-color 0.18s ease-out, background-color 0.18s ease-out,
			color 0.18s ease-out;
	}
	.opt:hover .opt-box {
		border-color: var(--teal);
	}
	.opt input:checked ~ .opt-box {
		border-color: var(--teal);
		background: var(--wash);
		color: var(--teal-deep);
		font-weight: 600;
	}
	.opt input:focus-visible ~ .opt-box {
		outline: 2px solid var(--teal);
		outline-offset: 2px;
	}

	/* ---- result --------------------------------------------------------- */
	.result-region {
		margin-top: var(--space-lg);
	}
	.progress-note {
		margin: 0 0 0 32px;
		font-size: 13px;
		letter-spacing: 0.04em;
		color: var(--text-2);
	}
	.result {
		border-top: 1px solid var(--line);
		padding-top: var(--space-lg);
	}
	.result-grid {
		display: grid;
		grid-template-columns: minmax(240px, 320px) 1fr;
		gap: var(--space-xl);
		align-items: start;
	}
	@media (max-width: 640px) {
		.result-grid {
			grid-template-columns: 1fr;
			gap: var(--space-lg);
		}
	}
	.result-label {
		margin: 0;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	.score {
		margin: 2px 0 var(--space-sm);
		display: flex;
		align-items: baseline;
		gap: 8px;
	}
	.score-n {
		font-size: 64px;
		font-weight: 700;
		line-height: 1;
		letter-spacing: -0.03em;
		font-variant-numeric: tabular-nums;
		color: var(--ink);
	}
	.score-of {
		font-size: 16px;
		font-weight: 500;
		color: var(--text-2);
	}
	.reading {
		margin: 0;
		max-width: 34ch;
		font-size: 16px;
		line-height: 1.55;
	}
	.reading strong {
		color: var(--teal-deep);
		font-weight: 700;
	}
	.source {
		margin: var(--space-md) 0 0;
		padding-top: var(--space-sm);
		border-top: 1px solid var(--line);
		font-size: 12px;
		line-height: 1.45;
		color: var(--text-2);
	}
	.source cite {
		font-style: normal;
		font-weight: 600;
		color: var(--ink);
	}

	/* ---- chart ---------------------------------------------------------- */
	.chartblock {
		margin: 0;
	}
	.chart {
		position: relative;
		height: 230px;
		padding: 10px 8px 26px 40px;
	}
	.grid-line.horizontal {
		position: relative;
		display: block;
		width: 100%;
		border-bottom: 1px solid var(--line);
	}
	.grid-line.horizontal span {
		position: absolute;
		left: -36px;
		bottom: -7px;
		font-size: 11px;
		color: var(--text-2);
		font-variant-numeric: tabular-nums;
	}
	.x-label {
		position: absolute;
		width: 3em;
		left: -1.5em;
		bottom: -22px;
		font-size: 11px;
		text-align: center;
		color: var(--text-2);
		font-variant-numeric: tabular-nums;
	}
	.curve {
		stroke: var(--teal);
		stroke-width: 2px;
		stroke-linejoin: round;
		stroke-linecap: round;
		fill: none;
	}
	.marker-halo {
		stroke: var(--teal);
		stroke-width: 22px;
		stroke-linecap: round;
		opacity: 0.18;
		fill: none;
	}
	.marker {
		stroke: var(--teal-deep);
		stroke-width: 11px;
		stroke-linecap: round;
		fill: none;
	}
	.you-label {
		position: absolute;
		transform: translate(-50%, -34px);
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--teal-deep);
	}
	figcaption {
		margin-top: var(--space-sm);
		padding-left: 40px;
		font-size: 12px;
		color: var(--text-2);
	}

	/* ---- the reveal -----------------------------------------------------
	   Two groups, not eight solos. The previous version staggered every element
	   separately (0/60/150/260/700/820ms) which, with the 650ms score tween and
	   the 620ms curve draw, gave ELEVEN distinct arrival moments spread over
	   1.24s. Rendering was a solid 60fps throughout — measured — so the
	   "assembling in bits" feel was choreography, not jank.

	   Now: the text column rises as one block, the curve draws, and the marker
	   lands as the line reaches it. Three moments, settled in ~600ms. */
	/* Group 1 — the text column, as a single block. */
	.result-label,
	.score,
	.reading,
	.source {
		animation: rise 300ms var(--ease-out) both;
	}
	/* Group 2 — the marker, timed to land while the line is still drawing past
	   it, so it reads as the curve arriving rather than a separate event. */
	/* The curve APPEARS; it is not drawn on. A stroke-dashoffset draw-on was
	   tried and removed: it set the dash inside requestAnimationFrame, so the
	   browser painted the full curve for one frame, hid it, and redrew it from
	   zero over 460ms — measured frame-by-frame as visible 1.0 -> 0.0 -> 1.0,
	   which the eye reads as "drawn, erased, redrawn". The original 2020 build
	   never animated the curve at all. A fade is one paint, no hidden frame. */
	/* Same TIMING as the text column, but its own keyframe. Two measured
	   traps here. (1) A separate opacity fade with an 80ms delay read as "text
	   fades, curve pops": the curve was at 0.05 while the text was at 0.80 on
	   the same frame. (2) Reusing the text's `rise` fixed that but its
	   translateY(6px) is in SVG user units inside Pancake's viewBox="0 0 100
	   100", so it scaled to 31px of on-screen travel — a jump, not a fade.
	   So: identical duration, easing and start, opacity only. Measured in
	   step with the text on every frame, zero travel. */
	.curve {
		animation: curve-in 300ms var(--ease-out) both;
	}
	@keyframes curve-in {
		from { opacity: 0; }
		to { opacity: 1; }
	}
	.marker,
	.marker-halo,
	.you-label,
	figcaption {
		animation: dot-in 260ms var(--ease-out) 340ms both;
	}
	.marker-halo {
		animation-name: halo-in;
	}
	.you-label {
		animation-name: label-in;
	}
	figcaption {
		animation-name: rise;
		animation-duration: 300ms;
	}
	@keyframes rise {
		from { opacity: 0; transform: translateY(6px); }
		to { opacity: 1; transform: none; }
	}
	@keyframes dot-in {
		from { opacity: 0; }
		to { opacity: 1; }
	}
	@keyframes halo-in {
		from { opacity: 0; }
		to { opacity: 0.18; }
	}
	@keyframes label-in {
		from { opacity: 0; transform: translate(-50%, -28px); }
		to { opacity: 1; transform: translate(-50%, -34px); }
	}

	/* ---- phones --------------------------------------------------------- */
	@media (max-width: 480px) {
		.pdq8 {
			padding: var(--space-sm) var(--space-md) var(--space-md);
			font-size: 15px;
		}
		.masthead {
			margin-bottom: var(--space-md);
		}
		h1 {
			font-size: 22px;
		}
		.lede {
			font-size: 14px;
		}
		.q {
			margin-bottom: var(--space-md);
		}
		.q-head {
			gap: var(--space-xs);
			margin-bottom: var(--space-xs);
		}
		.q-no {
			width: 21px;
			height: 21px;
			font-size: 11px;
		}
		.q-text {
			/* 14 x 1.5 = 21px, matching the badge again at this size. */
			font-size: 14px;
			line-height: 1.5;
		}
		/* Full-bleed: the badge indent costs 32px of width, and with five
		   columns that is the difference between one line and two per label. */
		/* PHONES: one option per row, not five across.
		   Measured at 351px (the modal's width on a 390px phone): five boxes
		   need 341px of text at 12px against 319px available, so the row could
		   only ever fit by shrinking to 10.5px and wrapping "Occasionally" onto
		   two lines — too small for an anxious older patient, and the wrap
		   broke the ordinal scale's shape. A vertical stack keeps the scale as
		   an ordered line (Never at the top, Always at the bottom), gives every
		   option a full 44px row at 15px, and uses the same dot-and-row idiom
		   as HFSGS and PD-Risk so the three tools share one gesture language.
		   The extra height is free under auto-height. */
		.scale {
			margin-left: 0;
			grid-template-columns: 1fr;
			/* 4px, not 6px: eight questions x five rows is a long column, and a
			   wider gap made it read as fifteen equal tiles rather than five
			   options belonging to one question. Tighter grouping keeps the
			   scale's shape; the 44px rows still give a full touch target. */
			gap: 4px;
		}
		.opt-box {
			justify-content: flex-start;
			gap: 10px;
			min-height: 44px;
			padding: 8px 12px;
			font-size: 15px;
			line-height: 1.4;
			text-align: left;
			border-radius: 8px;
		}
		.dot {
			display: block;
			flex: none;
			width: 18px;
			height: 18px;
			border-radius: 50%;
			border: 1.5px solid var(--line);
			background: #fff;
			transition: border-color 0.18s ease-out, box-shadow 0.18s ease-out;
		}
		.opt input:checked ~ .opt-box .dot {
			border-color: var(--teal);
			box-shadow: inset 0 0 0 4.5px var(--teal);
		}
		.progress-note {
			margin-left: 0;
		}
		.result-region {
			margin-top: var(--space-md);
		}
		.result {
			padding-top: var(--space-md);
		}
		.score-n {
			font-size: 52px;
		}
		.chart {
			height: 190px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.q-no,
		.opt-box {
			transition: none;
		}
		.curve,
		.result-label,
		.score,
		.reading,
		.source,
		figcaption,
		.marker,
		.you-label {
			animation: none;
			opacity: 1;
			transform: none;
		}
		.you-label {
			transform: translate(-50%, -34px);
		}
		.marker-halo {
			animation: none;
			opacity: 0.18;
		}
	}
</style>
